import { useEffect, useMemo, useState } from 'react'
import { useClub } from '../../club/ClubProvider.tsx'
import { localDate } from '../../lib/time.ts'
import { useJobAreas, useJobs, useTaskTypes, type JobAreaRow, type JobRow, type TaskTypeRow } from '../../data/plan.ts'
import { useSettingsConfirmations, type SettingsConfirmationRow } from '../../data/settingsDone.ts'
import { useCompletions, useCoverage, type CoverageRow, type JobCompletionRow } from '../../data/work.ts'

/** M25-A (docs/M25.md rule 2): WHO MAY READ THE HISTORY, and what the assistant's screens read. Everything the rule reads is already on the direction's device
 *  (jobs, job areas, task types, completions, coverage and confirmations, under `job.read` and `worklog.read`): no RPC, no server twin, no table.
 *  A crew phone holds its own finishes alone and the workstation none, so the history, the warnings and the panels are given ONLY where the viewer holds
 *  `job.plan` AND `worklog.read` and is not on a workstation: `useReadsWork` is that gate, and the pure functions take it as an argument. */
export function useReadsWork(): boolean {
  const { can, kiosk } = useClub()
  return !kiosk && can('job.plan') && can('worklog.read')
}

export interface SettingsData {
  jobs: JobRow[]; jobAreas: JobAreaRow[]; completions: JobCompletionRow[]; coverage: CoverageRow[]; confirmations: SettingsConfirmationRow[]; taskTypes: TaskTypeRow[]
}
/** The stores the assistant reads, or null until the device has answered for every one of them. */
export function useSettingsData(): SettingsData | null {
  const jobs = useJobs(), jobAreas = useJobAreas(), completions = useCompletions(), coverage = useCoverage(), confirmations = useSettingsConfirmations(), taskTypes = useTaskTypes()
  return useMemo(() => (jobs && jobAreas && completions && coverage && confirmations && taskTypes ? { jobs, jobAreas, completions, coverage, confirmations, taskTypes } : null),
    [jobs, jobAreas, completions, coverage, confirmations, taskTypes])
}

/** The site's today (the days of the rule are site days, never UTC). It follows the clock: a page left open past the site's midnight, or a phone woken up the
 *  next day, reads the new day (checked every 30 s and when the page becomes visible again), so the history window and « prévu d'ici » never lag a day behind. */
export function useToday(): string {
  const { site } = useClub()
  const tz = site.timezone
  const [today, setToday] = useState(() => localDate(new Date(), tz))
  useEffect(() => {
    const tick = (): void => setToday(localDate(new Date(), tz))
    tick()
    const id = setInterval(tick, 30_000)
    document.addEventListener('visibilitychange', tick)
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick) }
  }, [tz])
  return today
}
